import { useAtom, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { apiStatusAtom } from './apiStatus';
import { selectedJobAtom, selectedVersionAtom } from './company';

// Shared hook for recording API request status from any page.
// Each request entry: { name, page, kind, success, time, message, ts }
//   - name:  the API function name (e.g. 'loadCostDetailCondensed')
//   - page:  page identifier (e.g. 'bidForecast', 'burnToolEquipment'). Falls back to 'core'.
//   - kind:  'load' | 'save'
//   - time:  ms duration
//   - ts:    Date.now() when recorded
//
// Use:
//   const track = useApiTracker('bidForecast');
//   await track('loadCostDetailCondensed', 'load', () => loadCostDetailCondensed(params));

// Internal: write-only api status updater. Does NOT subscribe to apiStatusAtom,
// so callers don't re-render every time some other page logs a request. This is
// the version useApiTracker should compose with — pages that need to render the
// status (topMenu, ApiStatusIndicator) read the atom directly via useAtom.
const useApiStatusUpdate = () => {
    const setApiStatus = useSetAtom(apiStatusAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [selectedJob] = useAtom(selectedJobAtom);

    return useCallback((name, success, time, opts = {}) => {
        if (!selectedVersion || !selectedJob) return;
        const page = opts.page || 'core';
        const kind = opts.kind || 'load';
        const message = opts.message || '';
        const key = `${selectedVersion}_${selectedJob}`;
        setApiStatus(prev => {
            const prevEntry = prev[key] || { summary: {}, requests: [] };
            // Dedupe by (name + page) so the latest call wins per page.
            const newRequests = prevEntry.requests.filter(r => !(r.name === name && (r.page || 'core') === page));
            newRequests.push({ name, page, kind, success, time, message, ts: Date.now() });
            const allSuccess = newRequests.length > 0 && newRequests.every(r => r.success);
            return {
                ...prev,
                [key]: {
                    summary: { success: allSuccess },
                    requests: newRequests,
                },
            };
        });
    }, [selectedVersion, selectedJob, setApiStatus]);
};

// Back-compat: returns both the live apiStatus and the updater. Subscribes to
// apiStatusAtom — only call from components that actually render the status.
export const useApiStatus = () => {
    const [apiStatus] = useAtom(apiStatusAtom);
    const update = useApiStatusUpdate();
    return { apiStatus, update };
};

// Convenience: bind page + kind once per page. Does NOT subscribe to
// apiStatusAtom, so a save on one page won't re-render every other page that
// happens to use trackApi.
export const useApiTracker = (page) => {
    const update = useApiStatusUpdate();
    return useCallback(async (name, kind, runner) => {
        const t0 = performance.now();
        try {
            const res = await runner();
            update(name, true, Math.round(performance.now() - t0), { page, kind });
            return res;
        } catch (err) {
            update(name, false, Math.round(performance.now() - t0), { page, kind, message: err?.message || 'Error' });
            throw err;
        }
    }, [page, update]);
};
